import { useCallback, useEffect, useState } from "react";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import clinicAPI from "../../../services/clinicApi";
import { loadUpcomingTrainerSessions } from "../../../utils/trainerSessionUtils";
import authAPI from "../../../services/authApi";
import { useAdminHubUi } from "../../../hooks/useAdminAppMode";
import { formatDateRu, formatTimeRu } from "../../../utils/appointmentLabels";
import StatusBadge from "../ui/StatusBadge";
import AppointmentActions from "../AppointmentActions";
import AdminDoctorDataForm from "./AdminDoctorDataForm";
import AdminDoctorBranchesForm from "./AdminDoctorBranchesForm";
import AdminDoctorSchedule from "./AdminDoctorSchedule";
import AdminDoctorCalendar from "./AdminDoctorCalendar";
import AdminDoctorServices from "./AdminDoctorServices";
import AdminDoctorPublicProfile from "./AdminDoctorPublicProfile";
import AdminDoctorLimits from "./AdminDoctorLimits";
import AdminDoctorWaitlist from "./AdminDoctorWaitlist";
import AdminDoctorAudit from "./AdminDoctorAudit";
import TrainerProfilePwa from "./TrainerProfilePwa";
import {
  DOCTOR_TABS,
  EMPTY_EDIT,
  doctorToEdit,
  doctorPhotoUrl,
  DOCTORS_BASE,
  doctorBranchOptionsFromResponse,
} from "./doctorConstants";
import { PATIENTS_BASE } from "../AdminPatients/patientConstants";
import s from "../clinic.module.css";
import {
  Button,
  Card,
  Tabs,
  Avatar,
  EmptyState,
  useToast,
} from "../../ui";

const VALID_TABS = new Set(DOCTOR_TABS.map((t) => t.id));

export default function AdminDoctorProfile() {
  const { doctorId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const isApp = useAdminHubUi();

  const tabFromUrl = searchParams.get("tab");
  const doctorTab = VALID_TABS.has(tabFromUrl) ? tabFromUrl : "overview";

  const setDoctorTab = (id) => {
    const next = new URLSearchParams(searchParams);
    if (id === "overview") next.delete("tab");
    else next.set("tab", id);
    setSearchParams(next, { replace: true });
  };

  const [detail, setDetail] = useState(null);
  const [edit, setEdit] = useState(EMPTY_EDIT);
  const [branches, setBranches] = useState([]);
  const [directionCatalog, setDirectionCatalog] = useState([]);
  const [groupCatalog, setGroupCatalog] = useState([]);
  const [newPassword, setNewPassword] = useState("");
  const [loading, setLoading] = useState(true);
  const [workload, setWorkload] = useState(null);

  const loadDoctor = useCallback(() => {
    if (!doctorId) return;
    setLoading(true);
    clinicAPI
      .getDoctor(doctorId)
      .then((r) => {
        setDetail(r);
        const branchOptions = doctorBranchOptionsFromResponse(r.doctor);
        setEdit(doctorToEdit(r.doctor, branchOptions));
        setBranches(branchOptions);
      })
      .catch((e) => {
        toast.error(e.response?.data?.error || "Тренер не найден");
        navigate(isApp ? "/manage/app/team" : DOCTORS_BASE);
      })
      .finally(() => setLoading(false));
  }, [doctorId, navigate, toast, isApp]);

  useEffect(() => {
    clinicAPI.getSportDirections().then((r) => setDirectionCatalog(r.directions || [])).catch(() => {});
    clinicAPI
      .getSpecializations()
      .then((r) =>
        setGroupCatalog((r.specializations || []).filter((g) => g.is_active !== false)),
      )
      .catch(() => {});
  }, []);

  useEffect(() => {
    loadDoctor();
  }, [loadDoctor]);

  useEffect(() => {
    if (!doctorId) return;
    const month = new Date().toISOString().slice(0, 7);
    clinicAPI.getDoctorWorkload(doctorId, month).then(setWorkload);
  }, [doctorId]);

  const saveDoctor = async (e) => {
    e?.preventDefault?.();
    try {
      await clinicAPI.updateDoctor(doctorId, {
        full_name: edit.full_name,
        specialization_id: edit.specialization_id || null,
        description: edit.description,
        email: edit.email,
        is_active: edit.is_active,
        branchIds: edit.branchIds,
        directionIds: edit.directionIds,
        groupIds: edit.groupIds,
        experience_years: edit.experience_years,
        max_appointments_per_day: edit.max_appointments_per_day,
        min_break_minutes: edit.min_break_minutes,
        substitute_doctor_id: edit.substitute_doctor_id || null,
        notify_appointments: edit.notify_appointments,
        mis_external_id: edit.mis_external_id,
      });
      toast.success("Сохранено");
      loadDoctor();
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка");
    }
  };

  const saveLimits = () => saveDoctor();

  const saveBranches = async () => {
    try {
      await clinicAPI.updateDoctor(doctorId, {
        branchIds: edit.branchIds,
        directionIds: edit.directionIds,
        groupIds: edit.groupIds,
      });
      toast.success("Назначения сохранены");
      loadDoctor();
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка");
    }
  };

  const deactivate = async () => {
    let upcoming = 0;
    try {
      upcoming = await loadUpcomingTrainerSessions(doctorId);
    } catch {
      upcoming = 0;
    }
    const lines = [
      "Деактивировать тренера?",
      "Профиль скроется из записи, но вход в приложение останется, если аккаунт не заблокирован в разделе «Команда».",
    ];
    if (upcoming > 0) {
      lines.push(
        `У тренера ${upcoming} предстоящих занятий — они останутся в календаре, пока вы их не отмените.`,
      );
    }
    if (!window.confirm(lines.join("\n\n"))) return;
    try {
      await clinicAPI.deactivateDoctor(doctorId);
      toast.success("Тренер деактивирован");
      loadDoctor();
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка");
    }
  };

  const setPassword = async () => {
    const userId = detail?.doctor?.user_id;
    if (!userId) {
      toast.error("Нет привязанного аккаунта");
      return;
    }
    if (newPassword.length < 8) {
      toast.error("Пароль от 8 символов");
      return;
    }
    try {
      await authAPI.adminSetUserPassword(userId, newPassword);
      setNewPassword("");
      toast.success("Пароль обновлён");
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка");
    }
  };

  const toggleBranch = (id) => {
    setEdit((prev) => ({
      ...prev,
      branchIds: prev.branchIds.includes(id)
        ? prev.branchIds.filter((x) => x !== id)
        : [...prev.branchIds, id],
    }));
  };

  if (loading && !detail) {
    return (
      <div className={s.patientPage}>
        <p className={s.timelineLoading}>Загрузка…</p>
      </div>
    );
  }

  const doctor = detail?.doctor;
  const summary = detail?.summary;

  if (isApp) {
    return (
      <TrainerProfilePwa
        doctorId={doctorId}
        doctor={doctor}
        edit={edit}
        setEdit={setEdit}
        branches={branches}
        directionCatalog={directionCatalog}
        groupCatalog={groupCatalog}
        workload={workload}
        summary={summary}
        loading={loading}
        newPassword={newPassword}
        setNewPassword={setNewPassword}
        onSaveDoctor={saveDoctor}
        onSaveBranches={saveBranches}
        onSetPassword={setPassword}
        onDeactivate={deactivate}
        onReload={loadDoctor}
      />
    );
  }

  return (
    <div className={s.patientPage}>
      <nav className={`${s.patientBreadcrumbs} ${s.patientPageBlock}`}>
        <Link to="/manage/app/team">Команда</Link>
        <span aria-hidden>/</span>
        <span>{doctor?.full_name || "Тренер"}</span>
      </nav>

      <header className={`${s.patientProfileHeader} ${s.patientPageBlock}`}>
        <div className={s.patientProfileIdentity}>
          <Avatar
            name={doctor?.full_name}
            src={doctorPhotoUrl(doctor?.photo_url)}
            size="lg"
          />
          <div>
            <h1 className={s.patientProfileName}>
              {doctor?.full_name || "Тренер"}
            </h1>
            <div className={s.patientProfileMeta}>
              {doctor?.specialization && <span>{doctor.specialization}</span>}
              {doctor?.rating_avg != null && (
                <span>
                  ★ {Number(doctor.rating_avg).toFixed(1)} ({doctor.rating_count})
                </span>
              )}
              {doctor?.experience_years != null && (
                <span>Стаж {doctor.experience_years} лет</span>
              )}
              {doctor?.email && <span>{doctor.email}</span>}
              <span>{doctor?.is_active ? "Активен" : "Неактивен"}</span>
            </div>
          </div>
        </div>
        <div className={s.patientProfileActions}>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setDoctorTab("schedule")}
          >
            Расписание
          </Button>
        </div>
      </header>

      <Card className={s.patientPageBlock}>
        <Tabs
          items={DOCTOR_TABS}
          active={doctorTab}
          onChange={setDoctorTab}
        />

        {doctorTab === "overview" && (
          <>
            <div className={s.doctorStatsRow}>
              <div className={s.doctorStat}>
                <div className={s.doctorStatValue}>
                  {summary?.today_count ?? 0}
                </div>
                <div className={s.doctorStatLabel}>Записей сегодня</div>
              </div>
              <div className={s.doctorStat}>
                <div className={s.doctorStatValue}>
                  {summary?.week_count ?? 0}
                </div>
                <div className={s.doctorStatLabel}>На неделе</div>
              </div>
              <div className={s.doctorStat}>
                <div className={s.doctorStatValue}>
                  {summary?.has_schedule ? "Да" : "Нет"}
                </div>
                <div className={s.doctorStatLabel}>Расписание</div>
              </div>
              {workload && (
                <>
                  <div className={s.doctorStat}>
                    <div className={s.doctorStatValue}>
                      {workload.appointments_count}
                    </div>
                    <div className={s.doctorStatLabel}>
                      Записей в {workload.month}
                    </div>
                  </div>
                  <div className={s.doctorStat}>
                    <div className={s.doctorStatValue}>
                      {workload.total_hours}
                    </div>
                    <div className={s.doctorStatLabel}>Часов занятий</div>
                  </div>
                </>
              )}
            </div>
            {summary?.next_appointment ? (
              <p className={s.doctorNextAppt}>
                Ближайшее занятие:{" "}
                {formatDateRu(summary.next_appointment.start_datetime)} ·{" "}
                {formatTimeRu(summary.next_appointment.start_datetime)} —{" "}
                {summary.next_appointment.patient?.full_name}
              </p>
            ) : (
              <p className={s.doctorNextApptMuted}>Нет предстоящих записей</p>
            )}

            <h3 className={s.timelineSectionTitle}>Записи</h3>
            <div className={s.apptList}>
              {(detail?.appointments || []).length === 0 ? (
                <EmptyState title="Нет записей" />
              ) : (
                (detail?.appointments || []).slice(0, 15).map((a) => (
                  <div key={a.id} className={s.apptCard}>
                    <div style={{ flex: 1 }}>
                      <div className={s.apptDate}>
                        {formatDateRu(a.start_datetime)} ·{" "}
                        {formatTimeRu(a.start_datetime)}
                      </div>
                      <div className={s.apptMeta}>
                        {a.patient?.id ? (
                          <Link
                            to={`${PATIENTS_BASE}/${a.patient.id}`}
                            className={s.patientLink}
                          >
                            {a.patient?.full_name || "Клиент"}
                          </Link>
                        ) : (
                          a.patient?.full_name
                        )}{" "}
                        · {a.service?.name}
                      </div>
                      <div style={{ marginTop: 8 }}>
                        <StatusBadge status={a.status} />
                      </div>
                      <AppointmentActions
                        appointment={a}
                        role="admin"
                        compact
                        onUpdated={loadDoctor}
                      />
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        )}

        {doctorTab === "data" && (
          <AdminDoctorDataForm
            edit={edit}
            setEdit={setEdit}
            onSubmit={saveDoctor}
            newPassword={newPassword}
            setNewPassword={setNewPassword}
            onSetPassword={setPassword}
            onDeactivate={deactivate}
            isActive={doctor?.is_active}
          />
        )}

        {doctorTab === "branches" && (
          <AdminDoctorBranchesForm
            branches={branches}
            branchIds={edit.branchIds}
            onToggle={toggleBranch}
            directions={directionCatalog}
            groups={groupCatalog}
            directionIds={edit.directionIds}
            groupIds={edit.groupIds}
            onChangeAssignments={(patch) =>
              setEdit((prev) => ({ ...prev, ...patch }))
            }
            onSave={saveBranches}
          />
        )}

        {doctorTab === "calendar" && (
          <AdminDoctorCalendar
            doctorId={doctorId}
            doctorBranchOptions={branches}
          />
        )}

        {doctorTab === "schedule" && (
          <AdminDoctorSchedule
            doctorId={doctorId}
            doctorBranchOptions={branches}
            onScheduleSaved={loadDoctor}
          />
        )}

        {doctorTab === "services" && (
          <AdminDoctorServices doctorId={doctorId} />
        )}

        {doctorTab === "profile" && (
          <AdminDoctorPublicProfile
            doctorId={doctorId}
            doctor={doctor}
            edit={edit}
            setEdit={setEdit}
            onSave={saveDoctor}
            onPhotoUploaded={loadDoctor}
          />
        )}

        {doctorTab === "limits" && (
          <AdminDoctorLimits
            doctorId={doctorId}
            edit={edit}
            setEdit={setEdit}
            onSave={saveLimits}
          />
        )}

        {doctorTab === "waitlist" && (
          <AdminDoctorWaitlist
            doctorId={doctorId}
            doctorBranchOptions={branches}
          />
        )}

        {doctorTab === "audit" && (
          <AdminDoctorAudit doctorId={doctorId} />
        )}
      </Card>
    </div>
  );
}
